import React, { useEffect, useMemo, useState } from 'react'
import DataTable from '../../../ui/DataTable/DataTable'
import Switch from '../../../ui/Switch/Switch'
import ActiveStatusBadge from '../../../ui/ActiveStatusBadge/ActiveStatusBadge'
import ProductWithAvatar from '../../Products/components/ProductsList/ProductsTable/ProductWithAvatar'
import Price from '../../Products/components/ProductsList/Price'
import { FilterGroup, FilterSearch } from './GiftsFundTableFilters'
import { getProductAvatarFileName } from '../../../api/marketing/gifts/giftFundHelpers'
import {
  GIFT_FUND_BRAND_PARTICIPATION_FILTER,
  GIFT_FUND_BRAND_REMAIN_FILTER,
  GIFT_FUND_REMAINS_STATUS,
  catalogProductKey,
  filterBrandPoolMembers,
  getOnlineCatalogRemainQuantity,
} from '../../../api/marketing/gifts/giftFundCatalogPool'
import styles from './GiftsFund.module.scss'

const PARTICIPATION_OPTIONS = [
  { value: GIFT_FUND_BRAND_PARTICIPATION_FILTER.all, label: 'Усі' },
  { value: GIFT_FUND_BRAND_PARTICIPATION_FILTER.participating, label: 'Беруть участь' },
  { value: GIFT_FUND_BRAND_PARTICIPATION_FILTER.excluded, label: 'Виключені' },
]

const REMAIN_OPTIONS = [
  { value: GIFT_FUND_BRAND_REMAIN_FILTER.all, label: 'Усі' },
  { value: GIFT_FUND_BRAND_REMAIN_FILTER.inStock, label: 'В наявності' },
  { value: GIFT_FUND_BRAND_REMAIN_FILTER.outOfStock, label: 'Немає в наявності' },
]

const PAGE_SIZE = 25

const compareNullableNumber = (a, b) => {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return a - b
}

function GiftFundBrandPoolTable({
  members,
  excludedProductRefs,
  remainsByShop,
  remainsStatus,
  onToggleParticipation,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [participationFilter, setParticipationFilter] = useState(GIFT_FUND_BRAND_PARTICIPATION_FILTER.all)
  const [remainFilter, setRemainFilter] = useState(GIFT_FUND_BRAND_REMAIN_FILTER.all)
  const [page, setPage] = useState(0)

  const exclusionKeys = useMemo(
    () => new Set(excludedProductRefs.map((ref) => catalogProductKey(ref.source, ref.id))),
    [excludedProductRefs]
  )

  const filteredMembers = useMemo(
    () =>
      filterBrandPoolMembers({
        members,
        excludedProductRefs,
        remainsByShop,
        remainsStatus,
        searchQuery,
        participationFilter,
        remainFilter,
      }),
    [
      members,
      excludedProductRefs,
      remainsByShop,
      remainsStatus,
      searchQuery,
      participationFilter,
      remainFilter,
    ]
  )

  useEffect(() => {
    setPage(0)
  }, [searchQuery, participationFilter, remainFilter])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredMembers.length / PAGE_SIZE) - 1)
    if (page > maxPage) {
      setPage(maxPage)
    }
  }, [filteredMembers.length, page])

  const rows = useMemo(
    () =>
      filteredMembers.map((product) => {
        const remain = getOnlineCatalogRemainQuantity(
          product.id,
          'premium',
          remainsByShop,
          remainsStatus
        )
        return {
          ...product,
          excluded: exclusionKeys.has(catalogProductKey('premium', product.id)),
          remainQuantity: remain.status === GIFT_FUND_REMAINS_STATUS.ready ? remain.quantity : null,
          remainLabel:
            remain.status === GIFT_FUND_REMAINS_STATUS.ready && remain.quantity != null
              ? `${remain.quantity} шт.`
              : '—',
        }
      }),
    [exclusionKeys, filteredMembers, remainsByShop, remainsStatus]
  )

  const columns = useMemo(
    () => [
      {
        field: 'excluded',
        headerClassName: 'TableHeadCol',
        headerName: 'Участь',
        width: 92,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div
            className={styles.brandPoolSwitchCell}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              checked={!params.row.excluded}
              onChange={(participating) => onToggleParticipation(params.row, participating)}
              ariaLabel="Участь"
              title="Участь"
            />
          </div>
        ),
      },
      {
        field: 'name',
        headerClassName: 'TableHeadCol',
        headerName: 'Товар',
        flex: 1,
        minWidth: 280,
        renderCell: (params) => {
          const fileName = getProductAvatarFileName(params.row)
          return (
            <ProductWithAvatar
              id={params.row.id}
              avatar={fileName ? { fileName } : null}
              name={params.row.name || params.row.id}
              description={params.row.descriptionShort || ''}
            />
          )
        },
      },
      {
        field: 'barcode',
        headerClassName: 'TableHeadCol',
        headerName: 'Код товару',
        width: 150,
        valueGetter: (params) => params.row.barcode || '—',
      },
      {
        field: 'price',
        headerClassName: 'TableHeadCol',
        headerName: 'Ціна',
        width: 110,
        renderCell: (params) =>
          Number.isFinite(Number(params.row.price)) ? (
            <Price num={params.row.price} stroked={Boolean(params.row.specPrice)} />
          ) : (
            '—'
          ),
      },
      {
        field: 'remainQuantity',
        headerClassName: 'TableHeadCol',
        headerName: 'Наявність',
        width: 120,
        renderCell: (params) => params.row.remainLabel,
        sortComparator: compareNullableNumber,
      },
      {
        field: 'isActive',
        headerClassName: 'TableHeadCol',
        headerName: 'Статус товару',
        width: 130,
        renderCell: (params) =>
          params.row.isArchived ? (
            <ActiveStatusBadge variant="archived" />
          ) : (
            <ActiveStatusBadge isActive={params.row.isActive === true} />
          ),
        sortComparator: (a, b) => Number(Boolean(b)) - Number(Boolean(a)),
      },
    ],
    [onToggleParticipation]
  )

  return (
    <div className={styles.brandPoolTable}>
      <div className={styles.filters}>
        <FilterSearch value={searchQuery} onChange={setSearchQuery} />
        <FilterGroup
          label="Участь"
          value={participationFilter}
          options={PARTICIPATION_OPTIONS}
          onChange={setParticipationFilter}
        />
        <FilterGroup
          label="Наявність"
          value={remainFilter}
          options={REMAIN_OPTIONS}
          onChange={setRemainFilter}
        />
      </div>

      {members.length === 0 ? (
        <p className={styles.empty}>У бренда немає товарів основного каталогу</p>
      ) : rows.length === 0 ? (
        <p className={styles.empty}>Нічого не знайдено</p>
      ) : (
        <div className={styles.fundTable}>
          <DataTable
            key={`${searchQuery}|${participationFilter}|${remainFilter}`}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            table={{
              rows,
              columns,
              autoHeight: true,
              disableRowSelectionOnClick: true,
              paginationModel: { page, pageSize: PAGE_SIZE },
              onPaginationModelChange: (model) => setPage(model.page),
              pageSize: PAGE_SIZE,
            }}
          />
        </div>
      )}
    </div>
  )
}

export default GiftFundBrandPoolTable
